/**
 * Step 4: Data & Privacy
 * Collects data protection and privacy information
 */

import React from 'react';
import { Database } from '@phosphor-icons/react';
import { ToggleField, MultiSelectChips, StepSection, InfoCard } from './SharedComponents';

const DATA_PRIVACY_ROLES = [
  { id: 'controller', name: 'Controller (Responsible Party)', description: 'Determines the purpose and means of processing' },
  { id: 'processor', name: 'Processor (Operator)', description: 'Processes personal information on behalf of a controller' },
];

const COMMON_JURISDICTIONS = [
  { id: 'eu', name: 'European Union (GDPR)' },
  { id: 'uk', name: 'United Kingdom' },
  { id: 'us', name: 'United States' },
  { id: 'mauritius', name: 'Mauritius' },
  { id: 'namibia', name: 'Namibia' },
  { id: 'botswana', name: 'Botswana' },
  { id: 'nigeria', name: 'Nigeria' },
  { id: 'kenya', name: 'Kenya' },
  { id: 'uae', name: 'United Arab Emirates' },
  { id: 'singapore', name: 'Singapore' },
  { id: 'australia', name: 'Australia' },
  { id: 'other', name: 'Other' },
];

const Step4DataPrivacy = ({ formData, updateField }) => {
  return (
    <StepSection
      title="Data & Privacy"
      description="Define your data protection role and cross-border data flows"
      icon={Database}
    >
      {/* Data Privacy Role */}
      <div className="space-y-3">
        <label className="text-sm font-medium text-text-secondary">Organisation Data Role (POPIA)</label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {DATA_PRIVACY_ROLES.map(role => {
            const isSelected = formData.data_privacy_role === role.id;
            return (
              <button
                key={role.id}
                type="button"
                onClick={() => updateField('data_privacy_role', role.id)}
                className={`p-4 rounded-lg text-left transition-all ${
                  isSelected 
                    ? 'bg-primary text-primary-foreground ring-2 ring-primary ring-offset-2 ring-offset-background' 
                    : 'bg-background-surface border border-border hover:border-primary/50'
                }`}
                data-testid={`data-role-${role.id}`}
              >
                <span className={`text-sm font-medium ${isSelected ? 'text-primary-foreground' : 'text-text-primary'}`}>
                  {role.name}
                </span>
                <p className={`text-xs mt-1 ${isSelected ? 'text-primary-foreground/80' : 'text-text-tertiary'}`}>
                  {role.description}
                </p>
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Cross-Border Transfers */}
      <ToggleField
        label="Cross-Border Data Transfers"
        description="Does your organisation transfer personal information outside South Africa?"
        checked={formData.has_cross_border_transfers || false}
        onChange={(v) => updateField('has_cross_border_transfers', v)}
        testId="cross-border-toggle"
      />
      
      {formData.has_cross_border_transfers && (
        <div className="space-y-4 p-4 bg-background-surface rounded-lg border border-border">
          <MultiSelectChips
            label="Transfer Destinations"
            options={COMMON_JURISDICTIONS}
            selected={formData.cross_border_countries || []}
            onChange={(v) => updateField('cross_border_countries', v)}
            testId="cross-border-countries"
          />
          
          {formData.cross_border_countries?.includes('eu') && (
            <InfoCard variant="warning" title="GDPR Implications">
              Transferring data to/from the EU may require GDPR compliance. Consider Standard Contractual Clauses (SCCs) 
              or other appropriate safeguards.
            </InfoCard>
          )}
        </div>
      )}
      
      {/* POPIA Info */}
      {formData.data_privacy_role === 'controller' && (
        <InfoCard variant="info" title="Responsible Party Obligations">
          As a Responsible Party under POPIA, you must:
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Appoint an Information Officer</li>
            <li>Implement appropriate security safeguards</li>
            <li>Ensure lawful processing of personal information</li>
            <li>Maintain records of processing activities</li>
          </ul>
        </InfoCard>
      )}
      
      {formData.data_privacy_role === 'processor' && (
        <InfoCard variant="info" title="Operator Obligations">
          As an Operator under POPIA, you must:
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Process data only on instructions from the Responsible Party</li>
            <li>Implement appropriate security measures</li>
            <li>Notify the Responsible Party of any security compromises</li>
            <li>Maintain confidentiality of processed information</li>
          </ul>
        </InfoCard>
      )}
    </StepSection>
  );
};

export default Step4DataPrivacy;
